import * as Updates from 'expo-updates';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Linking } from 'react-native';

import { IDeepLinkData } from '@webtrader/core/appsflyer/types';
import storage, { EStorageKey } from '@webtrader/core/storage';

export const useEASUpdateSync = () => {
  const [hasEASUpdate, setHasEASUpdate] = useState(false);
  const [received, setReceived] = useState(0); // Received bytes will start at 0% (in percentage)

  const progressInterval = useRef<NodeJS.Timeout | number | undefined>(undefined);
  const timeoutId = useRef<NodeJS.Timeout | number | undefined>(undefined);

  const clearTimeoutAndInterval = useCallback(() => {
    if (progressInterval.current !== undefined) {
      clearInterval(progressInterval.current);
    }
    if (timeoutId.current !== undefined) {
      clearTimeout(timeoutId.current);
    }
  }, []);

  const reloadWithNewEASUpdate = useCallback(async () => {
    timeoutId.current = setTimeout(async () => {
      try {
        setReceived(100);
        storage.setItem(EStorageKey.IS_EAS_UPDATE_RELOAD, 'true');
        // Wait for onDeepLink() to persist data to MMKV before reloading.
        const initialUrl = await Linking.getInitialURL();
        if (initialUrl && !storage.getItem(EStorageKey.DEEP_LINK_DATA)) {
          await new Promise<void>((resolve) => {
            let elapsed = 0;
            const interval = setInterval(() => {
              elapsed += 500;
              if (storage.getItem(EStorageKey.DEEP_LINK_DATA) || elapsed >= 5000) {
                clearInterval(interval);
                resolve();
              }
            }, 500);
          });
        }

        // If deferred deep link, wait for MARKETING_PARAMS from onInstallConversionData (up to 5s)
        const deepLinkData = storage.getItem<IDeepLinkData>(EStorageKey.DEEP_LINK_DATA);
        if (deepLinkData?.isDeferred && !storage.getItem(EStorageKey.MARKETING_PARAMS)) {
          await new Promise<void>((resolve) => {
            let elapsed = 0;
            const interval = setInterval(() => {
              elapsed += 500;
              if (storage.getItem(EStorageKey.MARKETING_PARAMS) || elapsed >= 5000) {
                clearInterval(interval);
                resolve();
              }
            }, 500);
          });
        }

        await Updates.reloadAsync();
      } catch (error) {
        clearTimeoutAndInterval();
        setHasEASUpdate(false);
      }
    }, 1000);
  }, [clearTimeoutAndInterval]);

  const syncEASUpdates = useCallback(async () => {
    try {
      const update = await Updates.checkForUpdateAsync();
      if (update.isAvailable) {
        setHasEASUpdate(true);
        progressInterval.current = setInterval(() => {
          setReceived((prev) => {
            const next = Math.min(prev + 10 + Math.random() * 15, 99);
            if (next >= 99) {
              clearInterval(progressInterval.current);
            }
            return next;
          });
        }, 250);

        const result = await Updates.fetchUpdateAsync();

        if (result.isNew) {
          reloadWithNewEASUpdate();
        } else {
          clearTimeoutAndInterval();
          setHasEASUpdate(false);
        }
      }
    } catch (e) {
      clearTimeoutAndInterval();
      setHasEASUpdate(false);
    }
  }, [clearTimeoutAndInterval, reloadWithNewEASUpdate]);

  useEffect(() => {
    syncEASUpdates();
    return () => {
      clearTimeoutAndInterval();
    };
  }, [syncEASUpdates, clearTimeoutAndInterval]);

  return { hasEASUpdate, received };
};

export const useEASUpdateVersion = () => {
  const [currentEASUpdateVersion, setCurrentEASUpdateVersion] = useState('');

  const retrieveEASUpdateVersionId = useCallback(() => {
    const versionId = Updates.updateId ?? '';
    setCurrentEASUpdateVersion(versionId?.substring(0, 4));
  }, []);

  useEffect(() => {
    retrieveEASUpdateVersionId();
  }, [retrieveEASUpdateVersionId]);

  return { currentEASUpdateVersion };
};
